/* eslint-disable @typescript-eslint/no-explicit-any */
// PEG Expression Interpreter
import { Instruction } from "@atlaskit/pragmatic-drag-and-drop-hitbox/dist/types/tree-item";
import { Signal, signal } from "@preact/signals-react";
import * as dayjs from "dayjs";
import {
  clone,
  cloneDeep,
  every,
  fill,
  findIndex,
  fromPairs,
  groupBy,
  has,
  isArray,
  isBoolean,
  isEmpty,
  isNil,
  isNull,
  isNumber,
  isObject,
  isString,
  keyBy,
  map,
  merge,
  orderBy,
  partition,
  sortBy,
  isEqual,
  toPairs,
  zip,
  zipObject,
} from "lodash-es";
import { ListColumn } from "../../resolvers-types";
import QueryRequest from "../components/NGList/oDataQueryBuilder";
import {
  convertServiceResultToPropertiesEditor,
  getPropertiesEditorDataFromServiceResult,
  getRequestProtocolType,
  getServiceFromPath,
  parseOpenAPIServices,
  parseOpenAPIsimplified,
} from "../generators/codeGeneration/openApiParser";
import { generateFeature } from "../sampleData/ngEditorMenu";
import { moveMetadataItem } from "./actions";
import { getDefaultCCY, getFormData, GetFormatFromSite, getLocale, getRepeaterForm, setFormData } from "./dataService";
import { LogTag, log } from "./logger";
import {
  buildTreeItems,
  cloneJsonWithIDs,
  getItemLabel,
  getNodeChildRestrictions,
  getNodeParentRestrictions,
  getTypename,
  getTypenameUrl,
  isComponent,
  isContainer,
  isFeature,
  isMenu,
  mapMetadataRec,
} from "./metadataUtils";
import { isMobile, isNative } from "./native";
import { parse } from "./parser";
import { getTenant, getTenants } from "./tenantUtils";
import {
  camelCaseToTitleCase,
  flatLeveledList,
  flatNestedList,
  formatDate,
  formatNumber,
  generateGuid,
  generateUID,
  getEnvironmentBaseUrl,
  getRepositoryAssetsBaseUrl,
  groupByMany,
  isIndex,
  isNullOrEmpty,
  isSignal,
  makeSlug,
  parseAuthCookies,
  transformProRate,
} from "./utils";
import { generateAggregateObject, interpreterFunctions } from "./testHarnessUtils";

export const astCache: { [key: string]: object } = {};

const tag: LogTag = "interpreter";
export const metadataWrappers = [
  "Items",
  "ComponentContainer",
  "Tabs",
  "Features",
  "Groups",
  "Header",
  "ListColumns",
  "CellRendererItems",
  "ModalPopups",
  "ContentContainer",
];

export function getAst(expr) {
  if (!astCache[expr]) {
    const start = performance.now();
    astCache[expr] = parse(expr ?? "");
    const end = performance.now();
    const took = end - start;
    if (took > 5) {
      // ms
      log.info(tag, "parse:", expr, `took: ${end - start}ms`);
    }
  }
  return astCache[expr];
}

export type SetFn = (left: any, idx: any, right: any) => void;

export type InterpreterOptions = {
  merge: boolean;
  setFn?: SetFn;
  init?: boolean;
};

type InterpreterOptionsInner = {
  merge: boolean;
  setFn: SetFn;
  init: boolean;
};

const defaultOptions = { merge: false, setFn: undefined, init: true };

export function getExprValue(
  expr: string,
  scope: object,
  defaultValue: any,
  returnSignal = false,
  opts: InterpreterOptions = defaultOptions,
  error = {}
): any {
  return interpret(expr, scope, defaultValue, false, returnSignal, opts, error);
}

export function setExprValue(expr: string, scope: object, value: any, opts: InterpreterOptions = defaultOptions): any {
  return interpret(expr, scope, value, true, false, opts);
}

export function setBatchExprValue(exprValueList: { expr: string, value: any }[], scope: object, opts: InterpreterOptions = defaultOptions): any {
  if (!exprValueList || !Array.isArray(exprValueList) || exprValueList.length === 0) {
    return;
  }
  for (let i = 0; i < exprValueList.length; i++) {
    const { expr, value } = exprValueList[i];
    interpret(expr, scope, value, true, false, opts);
  }
}

const assignFn = (left: object | Array<any>, idx: string | number | null, right: any) => {
  if (isNil(idx)) return;
  left[idx] = right;
};

const mergeFn = (left: object | Array<any>, idx: string | number | null, right: any) => {
  if (isNil(idx)) return;
  merge(left[idx], right);
};

function interpret(
  expr: string,
  scope: object,
  defaultValue: any,
  setter: boolean,
  returnSignal: boolean,
  opts: InterpreterOptions = defaultOptions,
  error = {}
): any {
  try {
    // console.group("interpret", expr, defaultValue);
    const ast: any = getAst(expr);

    // Init options
    const innerOpts = merge(
      {
        setFn: opts.merge ? mergeFn : assignFn,
        init: true,
      },
      opts
    );

    const start = performance.now();
    const result = evaluateAst(ast, scope, defaultValue, setter, returnSignal, innerOpts);
    const end = performance.now();
    const took = end - start;
    // if (took > 2) {
    // ms
    log.info(tag, "evaluate", expr, `took: ${end - start}ms`);
    // }
    return result;
  } catch (e: any) {
    error.expr = expr;
    error.message = e.message;
    error.exception = e;
    log.error(tag, "error", expr, e.message);
    return null;
  }
}

function pathToId(root: any, targetId: string, path: (string | number)[] = []): (string | number)[] | null {
  if (!root || typeof root !== "object") return null;
  if (root.Id === targetId) return path;

  if (Array.isArray(root)) {
    for (let i = 0; i < root.length; i++) {
      const found = pathToId(root[i], targetId, [...path, i]);
      if (found) return found;
    }
  } else {
    for (const key of Object.keys(root)) {
      const found = pathToId(root[key], targetId, [...path, key]);
      if (found) return found;
    }
  }
  return null;
}

export function deepFindMeta(
  root: any,
  id: string,
  setter?: any,
  nextValue?: any,
  level?: any,
  topSignal?: any,
  opts?: InterpreterOptionsInner
) {
  // first try to find the metadata using the fast method
  const fast = findMeta(root, id, setter, nextValue, level, topSignal, opts);
  if (fast !== undefined && fast !== null) return fast;

  // slow fallback: brute-force search
  const path = pathToId(root, id);
  if (!path) return undefined;

  // walk the path to get the node itself
  let node: any = root;
  for (const segment of path) node = node[segment];
  return node;
}

export function findChild(
  obj: any,
  children: any,
  prop: any,
  value: any,
  setter?: any,
  nextValue?: any,
  level?: any,
  topSignal?: any,
  opts?: InterpreterOptionsInner
) {
  const props = isArray(children) ? children : [children];
  for (const childProp of props) {
    if (obj[childProp]) {
      if (isArray(obj[childProp])) {
        for (let i = 0; i < obj[childProp].length; i++) {
          const child = obj[childProp][i];
          if (child[prop] === value) {
            return { parent: obj, child: childProp, index: i, type: "array" };
          }
          const r = findChild(child, children, prop, value, setter, nextValue, level, topSignal, opts);
          if (r !== null && r.child !== null) return r;
        }
      } else if (isObject(obj[childProp]) && obj[childProp][prop] === value) {
        return { parent: obj, child: childProp, index: null, type: "object" };
      } else {
        const r = findChild(obj[childProp], children, prop, value, setter, nextValue, level, topSignal, opts);
        if (r !== null && r.child !== null) return r;
      }
    }
  }
  return { parent: obj, child: null, index: 0, type: "object" };
}

export function findMeta(
  obj: any,
  value: string,
  setter?: any,
  nextValue?: any,
  level?: any,
  topSignal?: any,
  opts?: InterpreterOptionsInner
) {
  const node = findChild(obj, metadataWrappers, "Id", value, setter, nextValue, level, topSignal, opts);

  if (setter && level == 0) {
    if (node.type == "array") {
      opts?.setFn(node?.parent?.[node.child], node.index, nextValue);
    } else if (node.type == "object") {
      opts?.setFn(node?.parent, node.child, nextValue);
    }
    if (topSignal) {
      // checks if topsignal.value has a key called "null", if yes, deletes it. Cover for updating parent.
      if (topSignal.value["null"]) {
        delete topSignal.value["null"];
      }
      topSignal.value = clone(topSignal.value);
    }
  }

  const foundNode = node?.parent?.[node?.child]?.[node?.index];

  return foundNode;
}

type FnContext = {
  setter?: any;
  nextValue?: any;
  level?: any;
  topSignal?: any;
  opts?: InterpreterOptionsInner;
};

function wrapWithContext<F extends (...args: any[]) => any>(
  fn: F
): (this: FnContext, ...args: Parameters<F>) => ReturnType<F> {
  return function (this: FnContext, ...args: Parameters<F>): ReturnType<F> {
    return fn(...args, this.setter, this.nextValue, this.level, this.topSignal, this.opts);
  };
}

function generatePageWithClonedMeta(obj, selectedNode, clonedNode, opts: InterpreterOptionsInner) {
  if (!selectedNode || !clonedNode) {
    return obj;
  }
  // recriate cloned component on paste, to avoid duplicated ids on multiple paste from same copied component
  const clonedComponent = cloneJsonWithIDs(clonedNode);

  const original = findChild(obj, metadataWrappers, "Id", clonedNode.Id, null, null, null, null, opts);
  const selected = findChild(obj, metadataWrappers, "Id", selectedNode["Id"], null, null, null, null, opts);

  const originalNode = original?.parent?.[original?.child]?.[original?.index] ?? null;
  const nodeRestrictions = getNodeChildRestrictions(selectedNode);
  const parentRestrictions = getNodeChildRestrictions(selected?.parent);
  const clonedRestrictions = getNodeParentRestrictions(clonedComponent);

  function getMoveMetadataItems(arg1, arg2, arg3) {
    return {
      DropData: {
        location: {
          current: {
            dropTargets: [
              {
                data: { id: arg1?.Id },
              },
            ],
          },
        },
        source: {
          data: {
            id: arg2.Id,
          },
        },
      },
      Page: arg3,
    };
  }

  // if cloned component is of __typename = component, don't do anything, this is not allowed
  if (isComponent(clonedComponent)) {
    return obj;
  }
  // if selected = original copied node => add copied node below selected node
  if (selectedNode?.Id === originalNode?.Id) {
    const instruction: Instruction = {
      type: "reorder-below",
      currentLevel: 0,
      indentPerLevel: 0,
    };
    const { Page } = moveMetadataItem(
      getMoveMetadataItems(selectedNode, clonedComponent, obj),
      { ...clonedComponent },
      instruction
    );
    return Page;
  }

  if (selectedNode?.Id !== originalNode?.Id && !isContainer(selectedNode)) {
    // when selectedNote is not equal original copied node, check parent restrictions
    if (!!parentRestrictions && parentRestrictions !== clonedComponent?.__typename) {
      return obj;
    }
    // check cloned component parent restrictions
    if (!!clonedRestrictions && clonedRestrictions !== selected?.parent?.__typename) {
      return obj;
    }
    const instruction: Instruction = {
      type: "reorder-below",
      currentLevel: 0,
      indentPerLevel: 0,
    };
    const { Page } = moveMetadataItem(
      getMoveMetadataItems(selectedNode, clonedComponent, obj),
      { ...clonedComponent },
      instruction
    );
    return Page;
  }

  // selected != copied and selected is container, create new component as last item of selected container
  if (selectedNode?.Id !== originalNode?.Id && isContainer(selectedNode)) {
    // when selected node is a container, check it's child restrictions
    if (!!nodeRestrictions && nodeRestrictions !== clonedComponent?.__typename) {
      return obj;
    }
    const instruction: Instruction = {
      type: "make-child",
      currentLevel: 0,
      indentPerLevel: 0,
    };
    const { Page } = moveMetadataItem(
      getMoveMetadataItems(selectedNode, clonedComponent, obj),
      { ...clonedComponent, id: clonedComponent?.Id },
      instruction
    );
    return Page;
  }

  return obj;
}

function deleteMeta(obj, value, setter, nextValue, level, topSignal, opts: InterpreterOptionsInner) {
  const node = findChild(obj, metadataWrappers, "Id", value, setter, nextValue, level, topSignal, opts);
  let deleted = null;
  if (setter && level == 0) {
    if (node.type == "array") {
      deleted = node.parent[node.child].splice(node.index, 1);
    } else if (node.type == "object") {
      deleted = node.parent[node.child];
      delete node.parent[node.child];
    }
    if (topSignal) {
      topSignal.value = clone(topSignal.value);
    }
  }
  return deleted;
}

function isSimpleType(o: any) {
  return o === null || typeof o !== "object" || o === undefined;
}

function getNGObjectType(param: any) {
  if (isSimpleType(param)) return "SimpleValue";

  // Check if the parameter is an array
  if (Array.isArray(param)) {
    for (let i = 0; i < param.length; i++) {
      const subType = getNGObjectType(param[i]);

      if (subType !== "SingleRecord") return "ComplexObject";
    }

    return "MultipleRows";
  }

  // Check if the parameter is an object but not null
  if (param !== null && typeof param === "object") {
    // Check every property to ensure they are all basic types
    for (const key in param) {
      if (Object.prototype.hasOwnProperty.call(param, key)) {
        const type = typeof param[key];
        if (
          type !== "string" &&
          type !== "number" &&
          type !== "boolean" &&
          param[key] !== null &&
          type !== "undefined"
        ) {
          return "ComplexObject";
        }
      }
    }
    return "SingleRecord";
  }

  return "ComplexObject";
}

function getNGResultType(param: any) {
  if (isSimpleType(param)) return "SimpleValue";

  // Check if the parameter is an object but not null
  if (param !== null && typeof param === "object") {
    if (!isNil(param["Items"]) && isArray(param["Items"])) {
      return "MultipleRows";
    }

    return "SingleRecord";
  }

  throw new Error("Invalid result type");
}

const getVerb = (event, PossibleVerbs: any[] | null) => {
  const verb = PossibleVerbs?.find((v) => v.path === event);
  return verb?.shortPath;
};

async function getRequiredField(state) {
  try {
    const openAPIData = state.OpenAPIData.value.paths;
    const tag = openAPIData[state.SelectedVerb.value].post.tags[0];
    const verb = state.SelectedRequestVerb?.value.toLowerCase();
    const action = verb ? verb.toLowerCase()[0].toUpperCase() + verb.slice(1) : "";
    const cookies = parseAuthCookies();
    const token = cookies["Api.Token"];
    const response = await fetch(`https://test.communify-lz.app/api/ModelRequireField/get`, {
      method: "POST",
      headers: {
        Tenant: "communify",
        authorization: `Bearer ${token}`,
        "content-type": "application/json",
        "user-agent": "insomnia/10.1.1",
      },
      body: JSON.stringify({
        ModelName: tag,
        Action: action,
      }),
    });
    if (!response.ok) {
      log.error(tag, "Fetch ModelRequireField error:", response.status);
      return null;
    }
    return await response.json();
  } catch (error) {
    log.error(tag, "Fetch ModelRequireField error:", error);
    return null;
  }
}

function getBindedField(selectedRequestVerb, fields, schema, tag) {
  if (["update", "delete"].includes(selectedRequestVerb)) {
    if (!fields.find((obj) => obj.Name === "Id")) {
      fields.push({
        Name: "Id",
        Bindings: {
          Value: `State.Selected${tag}Id`,
        },
      });
    }
  }
  if (selectedRequestVerb === "delete") return fields;

  const providedFieldsSet = new Set(fields.map((field) => field.Name));
  const requiredField = fields
    .filter((field) => field.Name.includes("Required") && field.Value)
    .map((field) => field.Name.replace("Required.", ""));

  let additionalFields = [];
  if (requiredField.length > 0) {
    additionalFields = schema
      .filter((name) => !providedFieldsSet.has(name) && requiredField.includes(name))
      .map((name) => ({
        Name: name,
        Bindings: {
          Value: `Form.${name}`,
        },
      }));
  } else {
    additionalFields = schema
      .filter((name) => !name.includes("Required.") && !providedFieldsSet.has(name))
      .map((name) => ({
        Name: name,
        Bindings: {
          Value: `Form.${name}`,
        },
      }));
  }

  return [...fields.filter((field) => !field.Name.includes("Required.")), ...additionalFields];
}
function getFieldsFromForm(form, sendEmptyFields, isOData, isODataQueryTab, baseQueryFields, isFormat) {
  if (!isOData) {
    const fields = Object.entries(form || {});

    const filteredFields = fields.filter(([key, field]) => {
      if (isNil(field)) return false; // this will never happen, as the fields are signals

      // Filter out fields that start with "Bindings"
      if (key.startsWith("Bindings")) return false;
      if (key.startsWith("Required")) return false;

      const bindings = fields.find(([k, v]) => k === `Bindings.${key}`);

      if (!sendEmptyFields) {
        if (
          !isBoolean((field as any).value) &&
          !isNumber((field as any).value) &&
          (isNullOrEmpty((field as any).value) && isNullOrEmpty((bindings?.[1] as any)?.value))
        ) {
          return false;
        }
      }

      return true;
    });

    const o = filteredFields.map(([key, field]) => {
      const bindings = fields.find(([k, v]) => k === `Bindings.${key}`);
      const required = fields.find(([k, v]) => k === `Required.${key}`);
      return {
        Name: key,
        Value: (field as any).value,
        ...(bindings?.[1]?.value ? { Bindings: { Value: bindings[1].value } } : {}),
        ...(required?.[1]?.value ? { Required: required[1].value } : {}),
      };
    });

    return o;
  }

  if (isODataQueryTab) {
    const formData = getFormData(form);
    // delete null values and empty arrays from formData
    for (const key in formData) {
      if (formData[key] === null || (Array.isArray(formData[key]) && formData[key].length === 0)) {
        delete formData[key];
      }
    }

    if (isEmpty(formData)) {
      return [];
    }

    if (formData["$select"]) {
      formData["$select"] = formData["$select"].join(",");
    }

    return [
      {
        Name: "OData",
        Value: formData,
      },
    ];
  }
  const input = baseQueryFields;

  if (isEmpty(input)) {
    return [];
  }

  const groupBy = {
    Name: "GroupBy",
    Value: (input?.GroupBy ?? []).map((group) => group.name),
  };

  const orderBy = {
    Name: "OrderBy",
    Value: (input?.OrderBy ?? []).map((order) => `${order.Name} ${order.Value}`),
  };

  const aggregate = {
    Name: "Aggregate",
    Value: generateAggregateObject(input?.Aggregate),
  };

  const select = {
    Name: "Select",
    Value: input?.Select,
  };

  const queryRequest = new QueryRequest();
  queryRequest.Filter = input?.Filter || [];
  queryRequest.GroupBy = groupBy.Value || [];
  queryRequest.OrderBy = orderBy.Value || [];
  queryRequest.Aggregate = aggregate.Value || [];
  queryRequest.Select = select.Value || [];

  const odataQuery = queryRequest.toODataQuery();

  let result = [
    {
      Name: "OData",
      Value: odataQuery,
    },
  ];
  if (isFormat) {
    const filters = (input?.Filter ?? []).map((filter) => ({
      Name: filter.Name,
      ...(filter.Operator ? { Operator: filter.Operator } : {}),
      Value: filter.Value,
    }));
    result = [{ Name: "Filter", Value: filters }, groupBy, orderBy, aggregate, select];
  }

  // remove empty fields and empty arrays from result
  return result.filter((field) => {
    if (field.Value === null || (Array.isArray(field.Value) && field.Value.length === 0)) {
      return false;
    }
    return true;
  });
}

function dynamicallySetObjectKey(key: string, value: string, tag?: string) {
  const cleanKey = tag || key.split(".")[0].replace(/^./, (c) => c.toUpperCase());
  return { [`State.${cleanKey}`]: value };
}

function filterResultItem(selectedRequestVerb: string) {
  if (selectedRequestVerb.includes("query")) return "Result.Items";
  if (["get", "create", "delete", "update"].some((verb) => selectedRequestVerb.includes(verb))) return "Result";
  return "Result.Item";
}

function addGetService(selectedPath, tag, Id) {
  const serviceName = selectedPath.split(".")[0];
  return [
    {
      Type: "Internal",
      Name: `${serviceName}.get`,
      Id: `${serviceName}.get`,
      Service: `${serviceName}.get`,
      ConfigBindings: {
        Refresh: "State.Refresh",
      },
      Fields: [
        {
          Name: "Id",
          ...(Id ? { Value: Id } : { Bindings: { Value: `State.Selected${tag}Id` } }),
        },
      ],
      Bindings: {
        [`State.Selected${tag}`]: "Result",
      },
      Refresh: null,
    },
  ];
}

function addDeleteService(selectedPath, tag, Id) {
  const serviceName = selectedPath.split(".")[0];
  return [
    {
      Type: "Internal",
      Name: `${serviceName}.delete`,
      Id: `${serviceName}.delete`,
      Service: `${serviceName}.delete`,
      Tag: tag,
      Trigger: "Action",
      Fields: [
        {
          Name: "Id",
          Value: Id || `State.Selected${tag}Id`,
        },
      ],
      Bindings: {
        [`State.${tag}`]: "Result",
      },
    },
  ];
}

const reduceNGProperties = (o: { [key: string]: any }, ignoredFields?: { [key: string]: any }) => {
  const transformed = {};

  if (isNil(o)) return transformed;

  Object.keys(o).forEach((key) => {
    if (ignoredFields?.[key]) {
      transformed[key] = o[key];
      return;
    }

    transformed[key] = null;
  });

  return transformed;
};



function getFieldsFromParameter(parameters?: string) {
  if (!parameters) return [];
  const fields = parameters.split(";").map((field) => {
    const [name, value] = field.split(":");
    // remove all situations of \n
    let formattedValue = value.replace(/\n/g, "").trim();
    const formattedName = name.replace(/\n/g, "").trim();
    const containsOperator = ["contains", "not contains", "startswith"];
    containsOperator.forEach((operator) => {
      if (formattedValue.includes(operator)) {
        const [field, op, val] = formattedValue.split(" ");
        formattedValue = `${op}(${field},'${val}')`;
      }
    });
    return { Name: formattedName, Value: formattedValue };
  });

  return fields;
}

function mergeTypename(entity) {
  const typename = getTypename(entity);
  if (!typename) return { ...entity, __typename: getTypenameUrl(entity) };
  return entity;
}

function mergeValuesIntoKeys(obj1, obj2) {
  const merged = { ...obj1 };
  for (const key in obj2) {
    if (Object.prototype.hasOwnProperty.call(merged, key)) {
      if (isArray(merged[key]) && isArray(obj2[key])) {
        merged[key] = Array.from(new Set([...merged[key], ...obj2[key]]));
      } else if (isObject(merged[key]) && isObject(obj2[key])) {
        merged[key] = mergeValuesIntoKeys(merged[key], obj2[key]);
      } else {
        merged[key] = obj2[key];
      }
    }
  }
  return merged;
}

const functions = {
  // Math
  min: Math.min,
  max: Math.max,
  abs: Math.abs,
  round: Math.round,
  floor: Math.floor,
  ceil: Math.ceil,
  // String
  toTitleCase: (str: string): string => {
    return camelCaseToTitleCase(str);
  },
  toLowerCase: String.prototype.toLowerCase,
  replace: String.prototype.replace,
  toUpperCase: String.prototype.toUpperCase,
  startsWith: String.prototype.startsWith,
  endsWith: String.prototype.endsWith,
  trim: String.prototype.trim,
  split: String.prototype.split,
  match: String.prototype.match,
  matchAll: String.prototype.matchAll,
  isNullOrEmpty: isNullOrEmpty,
  slug: makeSlug,
  toString(v) {
    return v.toString();
  },
  //DateTime
  toDate(date) {
    return new Date(date);
  },
  now: () => {
    return new Date();
  },
  clientTimezone: () => {
    return Intl.DateTimeFormat().resolvedOptions().timeZone;
  },
  nowAsISO: (userTimezone) => {
    const now = new Date();
    const dateOptions = { year: "numeric", month: "2-digit", day: "2-digit" };
    const timeOptions = {
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit",
      hour12: false,
    };

    const formattedDate = new Intl.DateTimeFormat("en-CA", dateOptions as any).format(now).replace(/\//g, "-");
    const formattedTime = new Intl.DateTimeFormat("en-CA", timeOptions as any).format(now).replace(/:/g, ":");

    return `${formattedDate}T${formattedTime}`;
  },
  nowAsString: () => {
    return new Date().toString();
  },
  today: () => {
    const today = new Date();
    return `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, "0")}-${String(today.getDate()).padStart(
      2,
      "0"
    )}`;
  },

  secondsToMinutes: (seconds: number): string => {
    if (!isNumber(seconds)) return "-"; // Not a number

    if (seconds < 60) return `${new Intl.NumberFormat("en-US").format(seconds)}s`; // Seconds only

    const minutes: number = Math.floor(seconds / 60);
    const hours: number = Math.floor(minutes / 60);
    const remainingMinutes: number = minutes % 60;

    const formatNumber = (num: number): string => new Intl.NumberFormat("en-US").format(num); // Format numbers

    if (hours > 0) {
      return remainingMinutes > 0
        ? `${formatNumber(hours)}h ${formatNumber(remainingMinutes)}m`
        : `${formatNumber(hours)}h`;
    }

    return `${formatNumber(minutes)}m`;
  },

  assetsUrl: () => {
    return getRepositoryAssetsBaseUrl();
  },
  envUrl: () => {
    return getEnvironmentBaseUrl();
  },
  //Objects
  clone: cloneDeep,
  toKvp: (o) => {
    const r: any[] = [];

    for (const key in o) {
      // eslint-disable-next-line no-prototype-builtins
      if (o.hasOwnProperty(key)) {
        r.push({ Key: key, Value: o[key] });
      }
    }

    return r;
  },
  keys: (o) => {
    return Object.keys(o);
  },
  setKey: (a, i, v) => {
    a[i] = v;
    return a;
  },
  removeProperty: (o, key) => {
    delete o[key];
    return o;
  },
  mergeTypename: mergeTypename,
  getNGObjectType: getNGObjectType,
  getNGResultType: getNGResultType,
  generateListColumn: generateListColumn,
  downloadAsCSV: downloadAsCSV,
  reduceNGProperties: reduceNGProperties,
  getFieldsFromParameter,
  merge: (o1, o2) => {
    return { ...o1, ...o2 };
  },
  mergeValuesIntoKeys,
  isEmpty: (o) => isNil(o) || isEmpty(o),
  isNil: (o) => isNil(o),
  // General
  parseFloat: parseFloat,
  parseInt: parseInt,
  parseBool: (v) => {
    return v && v.toString().toLowerCase() === "true" ? true : false;
  },
  // map: Array.prototype.map,
  filter: Array.prototype.filter,
  flatNestedList,
  flatLeveledList,
  filterProperty: (a, v, p) => a.filter((a1) => a1[p] === v),
  find: Array.prototype.find,
  // some: Array.prototype.some,
  some: (a, v) => a.some((a1) => a1.Id === v),
  someProperty: (a, v, p) => !isNil(a) && a.some((a1) => a1[p] === v),
  includes: Array.prototype.includes,
  join: Array.prototype.join,
  concat: Array.prototype.concat,
  slice: Array.prototype.slice,
  splice: (a, i, n) => {
    if (!a) return [];
    if (i < 0) i = a.length + i;
    return a.slice(0, i).concat(a.slice(i + n));
  },
  firstWord: (v) => {
    if (v) return v.split(" ")[0];
  },
  addToArray: (a, x) => {
    if (!a) a = [];
    return a.concat([x]);
  },
  toObject(collection, iteratee, initial) {
    return collection.reduce((acc, x) => (iteratee(acc, x), acc), initial);
  },
  zip: zip,
  zipObject: zipObject,
  _map: map,
  map: map,
  every: every,
  _every: every,
  sortBy: sortBy,
  _sortBy: sortBy,
  orderBy: orderBy,
  _orderBy: orderBy,
  findIndex: findIndex,
  _findIndex: findIndex,
  isArray: isArray,
  fromPairs: fromPairs,
  toPairs: toPairs,
  fill: fill,
  isEqual,
  keyBy: keyBy,
  partition: partition,
  groupBy: groupBy,
  groupByMany: groupByMany,
  isMenu: isMenu,
  isFeature: isFeature,
  getValueType: function (v) {
    return typeof v;
  },
  // RegExp
  test: RegExp.prototype.test,
  exec: RegExp.prototype.exec,
  guid: generateGuid,
  uid: generateUID,
  nfeature: generateFeature,
  // JSON
  jsonParse: JSON.parse,
  jsonStringify: JSON.stringify,
  getLastItemFromArray: (a) => {
    if (!a) return null;
    if (a.length === 0) return null;
    return a[a.length - 1];
  },
  // Custom
  isNative: isNative,
  isMobile,
  findChild: wrapWithContext(findChild),
  findMeta: wrapWithContext(findMeta),
  deepFindMeta: wrapWithContext(deepFindMeta),
  generatePageWithClonedMeta: generatePageWithClonedMeta,
  cloneJsonWithIDs: cloneJsonWithIDs,
  deleteMeta: wrapWithContext(deleteMeta),
  fromCharCode: (hex) => {
    return String.fromCharCode(parseInt(hex, 16));
  },
  dynamicallySetObjectKey: dynamicallySetObjectKey,
  filterResultItem: filterResultItem,
  getRequiredField: getRequiredField,
  generateServicesContent: (state, form) => {
    const selectedPath = state.SelectedPath?.value;
    const selectedRequestVerb = state.SelectedRequestVerb?.value?.toLowerCase?.();
    const fields = getFieldsFromForm(
      form,
      state.SendEmptyFields?.value,
      state.ODataRequestEnabled?.value,
      state.ODataQueryTab?.value,
      state.BaseQueryFields?.value,
      state.ODataRequestEnabled?.value
    );
    const openAPIData = state.OpenAPIData.value.paths;
    const tag = openAPIData[state.SelectedVerb.value]?.post?.tags?.[0];
    const Id = fields.find((i) => i.Name === "Id")?.Value;
    const requiredField = state?.RequiredField?.value?.RequiredFields || [];
    return [
      {
        Service: selectedPath,
        Fields: ["create", "update", "delete"].includes(selectedRequestVerb)
          ? getBindedField(selectedRequestVerb, fields, Object.keys(state.NGForm.RequestPropEditor), tag)
          : fields,
        Type: "Internal",
        Tag: tag,
        Name: selectedPath,
        Id: selectedPath,
        ...(["create", "update", "delete"].includes(selectedRequestVerb) && { Trigger: "Action" }),
        Bindings: dynamicallySetObjectKey(selectedPath, filterResultItem(selectedRequestVerb), tag),
      },
      ...(["update", "delete"].includes(selectedRequestVerb) ? addGetService(selectedPath, tag, Id) : []),
      ...(selectedRequestVerb === "update" ? addDeleteService(selectedPath, tag, Id) : []),
    ];
  },
  getTenant,
  getTenants,
  buildTreeItems,
  mapMetadataRec,
  //Formatting
  formatNumber: (a, b, c) => {
    const locale = getLocale();
    const defaultCCY = isNullOrEmpty(c) ? getDefaultCCY() : c;

    return formatNumber(locale, defaultCCY, a, b);
  },
  format: (value, formatName) => {
    const format = GetFormatFromSite(formatName);
    if (!isNil(format.NumberFormat)) {
      const ccy = getDefaultCCY();
      return formatNumber(getLocale(), ccy, value, format.NumberFormat);
    }

    if (!isNil(format.DateFormat)) return formatDate(getLocale(), value, format.DateFormat);
    return value;
  },
  formatDate: (a, b) => {
    const locale = getLocale();
    return formatDate(locale, a, b);
  },
  //helpers
  belowAbove: (value, threshold, belowStr, aboveStr, equalStr = aboveStr) => {
    if (value < threshold) {
      return belowStr;
    } else if (value > threshold) {
      return aboveStr;
    } else {
      return equalStr;
    }
  },
  getEntityName: getEntityName,
  getFormData: getFormData,
  setFormData,
  getRepeaterForm,
  // OpenAPI
  parseOpenAPIServices: parseOpenAPIServices,
  parseOpenAPIsimplified,
  getRequestProtocolType: getRequestProtocolType,
  getOpenAPIServiceFromPath: getServiceFromPath,
  getPageLocation: () => {
    return window.location;
  },
  getFieldsFromForm: getFieldsFromForm,
  getFieldsWithBindingsFromForm: (form, sendEmptyFields) => {
    form ||= {};
    const o = Object.entries(form)
      .filter(([key, field]: [string, any]) => {
        if (isNil(field)) return false; // this will never happen, as the fields are signals
        if (!sendEmptyFields)
          if (isNullOrEmpty(field.value) || (field.value !== true && isEmpty(field.value))) return false;
        if (key.startsWith("Bindings.")) return false;

        return true;
      })
      .map(([key, field]) => {
        return {
          Name: key,
          Value: (field as any).value,
        };
      });

    const bindings = Object.entries(form).filter(([key, field]: [string, any]) => {
      if (isNil(field)) return false; // this will never happen, as the fields are signals
      if (!sendEmptyFields)
        if (isNullOrEmpty(field.value) || (field.value !== true && isEmpty(field.value))) return false;
      if (!key.startsWith("Bindings.")) return false;

      return true;
    });

    bindings.forEach(([key, field]) => {
      const componentField: any = o.find((f) => f.Name === key.split(".")[1]);
      if (componentField) {
        componentField.Bindings = { Value: (field as any).value };
      }
    });

    return o;
  },
  getFieldsFromServiceResult: convertServiceResultToPropertiesEditor,
  getPropertiesEditorDataFromServiceResult: getPropertiesEditorDataFromServiceResult,
  fieldsToObject: (fields) => {
    const nonBindingsFields = fields?.reduce(function (field, x) {
      field[x.Name] = x.Value;
      return field ?? {};
    }, {});
    const bindingsFields = fields?.reduce(function (field, x) {
      if (x.Bindings) {
        field[`Bindings.${x.Name}`] = x.Bindings.Value;
      } else {
        return {};
      }

      field[x.Bindings] = x.Value;
      return field ?? {};
    }, {});

    return { ...bindingsFields, ...nonBindingsFields };
  },
  typeUseRows: (typename: string) => {
    const useRows = ["Repeater", "List", "Charts"];
    return useRows.includes(typename);
  },
  transformProRate,
  generateSampleData: (form, sampleData) => {
    // add logic for generate sample data
  },
  getVerb: getVerb,

  dayjs: dayjs,

  getItemLabel: (item: any, root: any) => {
    const type = getTypename(item);
    return getItemLabel(item, type, root, null);
  },
  ...interpreterFunctions
};

export function isStateVar(name: string | null) {
  return name != null && ["State", "Form", "Parent", "Global", "Repeater", "Grid"].includes(name);
}

const systemProps = ["NGForm", "NGDialog", "NGService", "NGRepeater", "NGComponent", "NGContextMenu"];

function isModule(obj) {
  return obj && typeof obj === "object" && obj[Symbol.toStringTag] === "Module";
}

function evaluateAst(
  ast: any,
  scope: any,
  defaultValue: any,
  setter: boolean,
  returnSignal: boolean,
  opts: InterpreterOptionsInner
) {
  let globalTopSignal: Signal | null = null;

  function evaluate(
    node: any,
    context: any,
    level: number,
    nextValue = defaultValue,
    allowTopSignalUpdate = true
  ): any {
    switch (node.type) {
      case "MemberExpression": {
        // *** STEP 1: Compute the property part first ***
        let property: any;
        if (node.computed) {
          // For computed properties, disallow updating the top signal.
          property = evaluate(node.property, context, level + 1, defaultValue, false);
        } else {
          property = node.property.type === "Identifier" ? node.property.name : node.property.value;
        }

        // *** STEP 2: Determine default value (newValue) based on property ***
        const isSearchIndex =
          node.computed &&
          node.property.type === "BinaryExpression" &&
          node.property.operator === "==" &&
          node.property.left.type === "Identifier";
        const newValue = isSearchIndex || isIndex(property) ? [] : {};
        const isFormKey =
          node.object &&
          node.object.object &&
          node.object.object.property &&
          node.object.object.property.name === "NGForm";

        // *** STEP 3: Now evaluate the object part using newValue as the default ***
        const object: any = evaluate(node.object, context, level + 1, newValue, true);
        if (object == null) return null;

        if (isStateVar(node.object.name)) {
          // Init value is necessary
          if (opts.init && !systemProps.includes(property)) {
            object[property] ||= signal(setter ? nextValue : level == 0 ? defaultValue : null);
          }
          // *** STEP 4: Lock in the global top signal if the base object is a state variable ***
          if (allowTopSignalUpdate && globalTopSignal === null) {
            globalTopSignal = object[property];
          }
        }

        // *** STEP 5: Handle array filtering syntax ***
        if (isSearchIndex) {
          const right: any = evaluate(node.property.right, context, level + 1, defaultValue, false);
          const left = node.property.left.name;
          let v = object.find((x: any) => x[left] === right);
          if (setter && isNil(v)) {
            v = { [left]: right };
            object.push(v);
          }
          return v;
        } else if (has(object, property)) {
          const v = object[property];
          if (setter) {
            if (isSignal(v)) {
              if (level === 0) {
                opts.setFn(v, "value", defaultValue);
              } else {
                v.value = isNil(v.value) ? newValue : v.value;
              }
            } else {
              if (level === 0) {
                opts.setFn(object, property, defaultValue);
                if (globalTopSignal) {
                  globalTopSignal.value = clone(globalTopSignal.value);
                }
              } else {
                object[property] = isNil(object[property]) ? newValue : object[property];
              }
            }
          }
          return !returnSignal && isSignal(v) ? v.value : v;
        } else if (isModule(object) && has(object.default, property)) {
          return object.default[property];
        } else if (has(object.__proto__, property)) {
          return {
            type: "MethodCall",
            object,
            method: object.__proto__[property],
          };
        } else if (has(context.functions, property)) {
          return {
            type: "MethodCall",
            object,
            method: context.functions[property],
          };
        } else {
          // *** STEP 6: Property does not exist on the object; create it if setter.
          if (setter) {
            if (level === 0) {
              opts.setFn(object, property, isFormKey ? signal(defaultValue) : defaultValue);
              if (globalTopSignal) {
                globalTopSignal.value = clone(globalTopSignal.value);
              }
            } else {
              (object as any)[property] = isFormKey ? signal(nextValue) : nextValue;
            }
            return nextValue;
          } else {
            return null;
          }
        }
      }
      case "Identifier": {
        const name = node.name;
        if (has(context.scope, name)) {
          const v = context.scope[name];
          return isSignal(v) ? v.value : v;
        } else if (has(context.functions, name)) {
          return context.functions[name];
        } else if (has(context.vars, name)) {
          return context.vars[name];
        } else {
          return null;
        }
      }
      case "Literal":
        return node.value;
      case "ConditionalExpression":
        return evaluate(node.test, context, level + 1, defaultValue, false)
          ? evaluate(node.consequent, context, level + 1, defaultValue, false)
          : evaluate(node.alternate, context, level + 1, defaultValue, false);
      case "BinaryExpression": {
        const left: any = evaluate(node.left, context, level + 1, defaultValue, false);
        const right: any = evaluate(node.right, context, level + 1, defaultValue, false);
        switch (node.operator) {
          case "*":
            return left * right;
          case "/":
            if (right === 0) throw new Error("Division by zero");
            return left / right;
          case "%":
            return left % right;
          case "+":
            return left + right;
          case "-":
            return left - right;
          case "<=":
            return left <= right;
          case ">=":
            return left >= right;
          case "<":
            return left < right;
          case ">":
            return left > right;
          case "===":
          case "==":
            return left === right;
          case "!==":
          case "!=":
            return left !== right;
          default:
            return null;
        }
      }
      case "LogicalExpression": {
        const left: any = evaluate(node.left, context, level + 1, defaultValue, false);
        const right: any = evaluate(node.right, context, level + 1, defaultValue, false);
        switch (node.operator) {
          case "&&":
            return left && right;
          case "||":
            return left || right;
          case "??":
            return left ?? right;
          default:
            return null;
        }
      }
      case "ObjectExpression": {
        const obj: any = {};
        for (const prop of node.properties) {
          const key = prop.key.type === "Identifier" ? prop.key.name : prop.key.value;
          obj[key] = evaluate(prop.value, context, level + 1, defaultValue, false);
        }
        return obj;
      }
      case "ArrayExpression":
        return node.elements.map((x: any) => evaluate(x, context, level + 1, defaultValue, false));
      case "FunctionExpression": {
        return function (...args: any) {
          const vars: any = {};
          for (let i = 0; i < node.params.length; i++) {
            vars[node.params[i].name] = args[i];
          }
          const childContext = {
            functions: context.functions,
            scope: context.scope,
            vars: vars,
          };
          return evaluate(node.body, childContext, level + 1, defaultValue, false);
        };
      }
      case "CallExpression": {
        const callee: any = evaluate(node.callee, context, level + 1, defaultValue, false);
        if (callee == null) return null;

        const args: any[] = node.arguments.map((x: any) => evaluate(x, context, level + 1, defaultValue, false));

        if (typeof callee === "function") {
          const ctx = { setter, nextValue, level, globalTopSignal, opts };
          const res = callee.bind(ctx)(...args);
          return isSignal(res) ? res.value : res;
        } else if (callee && callee.type === "MethodCall") {
          const res = callee.method.apply(callee.object, args);
          return isSignal(res) ? res.value : res;
        } else if (isModule(callee)) {
          const res = callee.default(...args);
          return isSignal(res) ? res.value : res;
        }

        return null;
      }
      case "UnaryExpression": {
        const arg: any = evaluate(node.argument, context, level + 1, defaultValue, false);
        switch (node.operator) {
          case "~":
            return ~arg;
          case "+":
            return +arg;
          case "-":
            return -arg;
          case "!":
            return !arg;
          default:
            return null;
        }
      }
      case "IfStatement":
        return evaluate(node.test, context, level + 1, defaultValue, false)
          ? evaluate(node.consequent, context, level + 1, defaultValue, false)
          : evaluate(node.alternate, context, level + 1, defaultValue, false);
      case "BlockStatement":
        return evaluate(node.body, context, level + 1, defaultValue, false);
      default:
        return null;
    }
  }

  const result = evaluate(ast, { scope, functions }, 0, defaultValue, true);
  return returnSignal ? globalTopSignal : result;
}

export function traverseAst(node, callback) {
  function visit(node, parent = null) {
    // Call the visitor function for the current node
    callback(node, parent);

    // Based on the node type, visit its children or related nodes
    switch (node.type) {
      case "MemberExpression":
        visit(node.object, node);
        visit(node.property, node);
        break;

      case "Identifier":
      case "Literal":
        // These are leaf nodes, but the visitor function is called for them above
        break;

      case "BinaryExpression":
      case "LogicalExpression":
        visit(node.left, node);
        visit(node.right, node);
        break;

      case "ConditionalExpression":
        visit(node.test, node);
        visit(node.consequent, node);
        visit(node.alternate, node);
        break;

      case "CallExpression":
        visit(node.callee, node);
        node.arguments.forEach((arg) => visit(arg, node));
        break;

      case "UnaryExpression":
        visit(node.argument, node);
        break;

      case "ArrayExpression":
        node.elements.forEach((element) => visit(element, node));
        break;

      case "ObjectExpression":
        node.properties.forEach((prop) => {
          visit(prop.key, node);
          visit(prop.value, node);
        });
        break;

      case "FunctionExpression":
        node.params.forEach((param) => visit(param, node));
        visit(node.body, node);
        break;

      case "BlockStatement":
        node.body.forEach((statement) => visit(statement, node));
        break;

      case "IfStatement":
        visit(node.test, node);
        visit(node.consequent, node);
        if (node.alternate) {
          visit(node.alternate, node);
        }
        break;

      // Add any additional cases that might be relevant
      // ...

      default:
        // Log or handle unimplemented node types, if necessary
        console.warn(`Unhandled node type: ${node.type}`);
    }
  }

  visit(node, null); // Start traversal from the root node
}

// function to transform pascal cased sting to natural string (ie: PascalCased => Pascal Cased, Pascal123Cased => Pascal 123 Cased)
function getNaturalStringFromPascalCased(string) {
  return string.replace(/([A-Z0-9])/g, " $1").trim();
}
export function generateListColumn(result) {
  const items = result.Items;
  const listColumns: ListColumn[] = [];
  const keys = Object.keys(items[0]);
  keys.forEach((key) => {
    listColumns.push({
      __typename: "ListColumn",
      Id: key,
      HeaderName: getNaturalStringFromPascalCased(key),
      Flex: 1,
      ColumnMinimumWidth: 150,
      Name: key,
      IsPrimaryKey: key.toLowerCase() === "id",
    });
  });
  return listColumns;
}

export function downloadAsCSV(jsonArray: any[], str = "data"): void {
  // replace n '.' into '-'
  const path = str.replace(/\./g, "-");
  const fileName = `${path}.csv`;
  if (!jsonArray || jsonArray.length === 0) {
    console.error("Empty JSON array");
    return;
  }

  const headers = Object.keys(jsonArray[0]);

  const csvRows = jsonArray.map((item) =>
    headers
      .map((header) => {
        const value = item[header] ? String(item[header]).replace(/"/g, '""') : ""; // Escape quotes in values
        return `"${value}"`;
      })
      .join(",")
  );

  const csvString = [headers.join(","), ...csvRows].join("\n");

  const blob = new Blob([csvString], { type: "text/csv;charset=utf-8;" });
  const link = document.createElement("a");
  const url = URL.createObjectURL(blob);
  link.href = url;
  link.setAttribute("download", fileName);
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
}

export function collectFormProperties(expr) {
  const ast = getAst(expr);
  const props: string[] = [];
  traverseAst(ast, (node) => {
    if (node.type === "MemberExpression" && node.object.type === "Identifier" && node.object.name === "Form") {
      const prop = node.property.name || node.property.value;
      props.push(prop);
    }
  });
  return props;
}

export function getEntityName(selectedPath) {
  const name = selectedPath.split(".")[0];
  return name.charAt(0).toUpperCase() + name.slice(1);
}

// export function collectFormProperties(expr) {
//   const ast = getAst(expr);
//   const props: string[] = [];

//   // Assuming traverseAst is a generic AST traversal function that calls
//   // the provided callback for every node in the AST.
//   traverseAst(ast, (node) => {
//     if (node.type === "MemberExpression") {
//       const propertyPath = buildPropertyChain(node);
//       if (propertyPath.startsWith("Form.")) {
//         props.push(propertyPath.substring(5)); // Remove the 'Form.' part to store the property chain
//       }
//     }
//   });

//   function buildPropertyChain(node) {
//     let current = node;
//     let pathComponents: string[] = [];
//     while (current && current.type === "MemberExpression") {
//       if (current.property.type === "Identifier") {
//         pathComponents.unshift(current.property.name);
//       } else if (current.property.type === "Literal") {
//         pathComponents.unshift(current.property.value);
//       }
//       current = current.object;
//     }
//     if (current.type === "Identifier") {
//       pathComponents.unshift(current.name);
//     }
//     return pathComponents.join(".");
//   }

//   console.log(`~~Found property related to Form: `, props);
//   return props;
// }
// export function collectFormProperties(expr) {
//   const ast = getAst(expr);
//   const props = new Set();

//   function isTerminalMemberExpression(node) {
//     // A terminal MemberExpression has a MemberExpression as its parent,
//     // but only if this node's object is directly referenced by the parent.
//     // This function checks if the node is not used as an object by another MemberExpression,
//     // making it a terminal or leaf MemberExpression in the property chain.
//     return !(node.parent && node.parent.type === "MemberExpression" && node.parent.object === node);
//   }

//   function buildPropertyChain(node) {
//     let path: string[] = [];
//     let current = node;

//     while (current && current.type === "MemberExpression") {
//       // Collect property names, diving up the chain
//       const propName = current.property.type === "Identifier" ? current.property.name : current.property.value;
//       path.unshift(propName); // Add property name at the beginning of the path array
//       current = current.object; // Move up the chain
//     }

//     // Ensure the chain starts with 'Form'
//     if (current && current.type === "Identifier" && current.name === "Form") {
//       path.unshift("Form"); // Include 'Form' at the start of the path
//       return path.join(".");
//     }
//     return "";
//   }

//   traverseAst(ast, (node, parent) => {
//     // Annotate each node with its parent for context in checks
//     node.parent = parent;

//     if (node.type === "MemberExpression" && isTerminalMemberExpression(node)) {
//       const propertyPath = buildPropertyChain(node);
//       if (propertyPath.startsWith("Form.")) {
//         // Remove the 'Form.' part to store only the property chain
//         props.add(propertyPath.substring(5));
//       }
//     }
//   });

//   // Convert the Set to an array to return
//   console.log(`~~Found property related to Form: `, props);
//   return Array.from(props);
// }
